import React, {
  useState,
  useContext,
  Fragment,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import {
  Typography,
  Button,
  TextField,
  CardMedia,
  Modal,
  IconButton,
  Select,
  MenuItem,
  Avatar,
} from "@material-ui/core";
import CloseIcon from "../icons/btnClose";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import SignatureCanvas from "react-signature-canvas";
import { SnackbarContext } from "../constant/SnackbarProvider";
import { useGlobalData } from "../constant/GlobalDataContext";
import { createSignSvgTemplate, signatureRawToJSON } from "../utils";

type Props = {
  open?: boolean;
  setOpen?: (v: boolean) => void;
  type: string;
  goBack?: () => void;
  SignTrigger?: (png: any, svg: any) => void;
  staffInfo?: any;
};

const ModalContainer = (props: Props) => {
  const { open, setOpen, type, goBack, SignTrigger, staffInfo } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const Snackbar = useContext(SnackbarContext);
  const [disableSign, setDisableSign] = useState(true);
  const [brand, setBrand] = useState("");
  const [area, setArea] = useState("");
  const textFieldRefs = useRef([]);
  const sigCanvas = useRef<SignatureCanvas>(null);
  const sigCanvasParent = useRef<any>(null);
  const [pwLists, setPwLists] = useState(["", "", "", "", "", ""]);
  const { globalData, setGlobalData } = useGlobalData();

  // const areaLists = ["香港分店", "香港辦公室"];

  const pwCorrect = "123611";
  const _pm = require("../icons/perfectMedical.png");
  const _goku = require("../icons/goku.png");

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleClear = () => {
    sigCanvas.current.clear();
    setDisableSign(true);
  };

  const handleGenerate = () => {
    if (SignTrigger !== undefined) {
      const signaturePad = sigCanvas.current.getSignaturePad();
      const svgTemplate = createSignSvgTemplate(signaturePad.toData(), {
        svgWidth: sigCanvasParent.current.clientWidth,
        svgHeight: sigCanvasParent.current.clientHeight,
      });
      let svg = decodeURIComponent(signatureRawToJSON(svgTemplate));

      const pngDataUrl = sigCanvas.current
        .getTrimmedCanvas()
        .toDataURL("image/png");

      // Trigger the callback with both PNG and transformed SVG data
      SignTrigger(pngDataUrl, svg);
      setOpen(false);
      setDisableSign(true);
    }
  };

  const RenderTitle = () => {
    if (
      type === "noRecord" ||
      type === "logout" ||
      type === "onlyUpdateOnce" ||
      type === "hrConfirmed" ||
      type === "noSignature" ||
      type === "noSignDocument" ||
      type === "noAuthorization"
    )
      return "提示";
    if (type === "isNeedPW") return "請職員輸入密碼";
    if (type === "select" || type === "addNewForm") return "請選擇公司";
    if (type === "sign") return "請簽署";
    if (type === "updateForm") return "選擇地區";
    return "";
  };

  const RenderArea = (v: string) => {
    if (v === "hkOffice") return "香港辦公室";
    if (v === "hkShop") return "香港分店";
    return "";
  };

  const areaLists = useMemo(() => {
    const filteredData =
      user?.data?.userDataFilter?.filter(
        (o) => o.controlArea === "c&b" || o.controlArea === "recruitment"
      ) || [];

    const uniqueControlValues = filteredData.filter(
      (value, index, self) =>
        index === self.findIndex((t) => t.controlValue === value.controlValue)
    );
    return uniqueControlValues;
  }, [user]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, i: number) => {
      const value = e.target.value;
      const singleDigitRegex = /^[0-9]$/;
      if (singleDigitRegex.test(value) || value === "") {
        const newPwLists = [...pwLists];
        newPwLists[i] = value;
        setPwLists(newPwLists);

        // Move to the next TextField if value is entered
        if (value !== "" && i < pwLists.length - 1) {
          textFieldRefs.current[i + 1]?.focus();
        }
      }
    },
    [pwLists]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
      if (e.key === "Backspace" && pwLists[i] === "" && i > 0) {
        const newPwLists = [...pwLists];
        newPwLists[i - 1] = "";
        setPwLists(newPwLists);
        textFieldRefs.current[i - 1]?.focus();
      }
    },
    [pwLists]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault(); // Prevents the default form submission behavior
      const pw = pwLists.join("");
      if (pw === pwCorrect) {
        goBack();
      } else {
        Snackbar.open({
          alertProps: { severity: "error" },
          message: "密碼錯誤",
        });
      }
    },
    [pwLists, pwCorrect, goBack, Snackbar]
  );

  const renderPwFields = useMemo(() => {
    return (
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <div>
          {pwLists?.map((v, i) => (
            <TextField
              key={i}
              autoComplete="off"
              variant="outlined"
              type="tel"
              inputMode="numeric"
              inputRef={(el) => (textFieldRefs.current[i] = el)}
              onChange={(e) => handleChange(e as any, i)}
              onKeyDown={(e) => handleKeyDown(e as any, i)}
              value={v}
              style={{
                width: "4rem",
                margin: "3rem 0.5rem",
              }}
              InputProps={{
                classes: {
                  input: classes.customOutlinedInput,
                },
              }}
            />
          ))}
        </div>
        <Button
          type="submit" // Changes the button type to submit
          variant="contained"
          color="primary"
          disabled={pwLists.join("").length !== 6}
          className={classes.button}
          style={{ marginTop: "2rem" }}
        >
          確認
        </Button>
      </form>
    );
  }, [
    handleSubmit,
    pwLists,
    classes.button,
    classes.customOutlinedInput,
    handleChange,
    handleKeyDown,
  ]);

  useEffect(() => {
    if (!open) {
      setPwLists(["", "", "", "", "", ""]);
    }
  }, [open]);

  return (
    <Fragment>
      <Modal className={classes.modal} open={open}>
        <div className={classes.modalContainer}>
          <div className={classes.modalHeader}>
            <IconButton
              className={classes.imageClose}
              onClick={() => {
                setOpen(false);
                setBrand("");
                setArea("");
                setPwLists(["", "", "", "", "", ""]);
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography className={classes.modalNavTitle}>
              {RenderTitle()}
            </Typography>
          </div>
          <div className={classes.modalBody}>
            {type === "select" && (
              <div style={{ display: "flex" }}>
                <CardMedia
                  onClick={() => {
                    navigate("/main/linking", { state: { brand: "PM" } });
                  }}
                  component="img"
                  image={_pm}
                  alt=""
                  className={classes.media}
                />
                <CardMedia
                  onClick={() => {
                    navigate("/main/linking", { state: { brand: "GOKU" } });
                  }}
                  component="img"
                  image={_goku}
                  alt=""
                  className={classes.media}
                />
              </div>
            )}
            {type === "noRecord" && (
              <Typography className={classes.noRecord}>
                {"沒有該員工紀錄"}
              </Typography>
            )}
            {type === "noAuthorization" && (
              <Typography className={classes.noRecord}>
                {"沒有權限瀏覽該員工紀錄"}
              </Typography>
            )}

            {type === "onlyUpdateOnce" && (
              <Typography className={classes.noRecord}>
                {"此員工已完成確認個人資料表格"}
              </Typography>
            )}
            {type === "noSignature" && (
              <Typography className={classes.noRecord}>
                {"此員工還未完成簽署個人資料表格"}
              </Typography>
            )}
            {type === "hrConfirmed" && (
              <Typography className={classes.noRecord}>
                {"已完成簽署入職文件"}
              </Typography>
            )}
            {type === "noSignDocument" && (
              <Typography className={classes.noRecord}>
                {"此員工還未完成簽署3份文件"}
              </Typography>
            )}

            {type === "logout" && (
              <div>
                <Typography className={classes.noRecord}>
                  {"是否確認登出？"}
                </Typography>
                <Button
                  variant="contained"
                  color={"primary"}
                  className={classes.button}
                  onClick={() => {
                    localStorage.removeItem("user");
                    navigate("/login");
                  }}
                >
                  確認
                </Button>
              </div>
            )}
            {type === "sign" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <div className={classes.signature} ref={sigCanvasParent}>
                  <SignatureCanvas
                    onBegin={(v) => {
                      setDisableSign(false);
                    }}
                    canvasProps={{
                      width: 500,
                      height: 200,
                      className: "sigCanvas",
                    }}
                    ref={sigCanvas}
                  />
                  <CardMedia
                    onClick={handleClear}
                    component="img"
                    image={require("../icons/reset.png")}
                    alt=""
                    className={classes.resetImage}
                  />
                </div>
                <Button
                  variant="contained"
                  color={"primary"}
                  className={classes.button}
                  style={{ marginTop: "2rem" }}
                  onClick={handleGenerate}
                  disabled={disableSign}
                >
                  確認
                </Button>
              </div>
            )}
          {type === "updateForm" && staffInfo && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Avatar
                    alt=""
                    src={staffInfo?.brand === "PM" ? _pm : _goku}
                    style={{
                      width: "80px",
                      height: "80px",
                    }}
                  />
                  <Typography style={{ paddingLeft: "3rem",fontSize: "24px" }}>
                    {staffInfo?.chinese_name}
                  </Typography>
                </div>
                <Select
                  variant="outlined"
                  onChange={(event: any) => {
                    setArea(event.target.value);
                  }}
                  value={area}
                  className={classes.searchSelect}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    所屬地區
                  </MenuItem>
                  {areaLists?.map((v, i) => {
                    return (
                      <MenuItem key={i} value={v?.controlValue}>
                        {RenderArea(v?.controlValue)}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Button
                  variant="contained"
                  color={"primary"}
                  className={classes.button}
                  style={{ marginTop: "2rem", alignSelf: "center" }}
                  disabled={area === ""}
                  onClick={() => {
                    setGlobalData({
                      ...globalData,
                      targetInfo: staffInfo,
                      isHaveData: staffInfo?.length > 0 ? true : false,
                      brand: staffInfo?.brand,
                      area,
                      needPW: true,
                    });
                    navigate("/p/StaffInfoFormUnsigned", {
                      state: {
                        targetInfo: staffInfo,
                        isHaveData: staffInfo?.length > 0 ? true : false,
                        brand: staffInfo?.brand,
                        area,
                        needPW: true,
                      },
                    });
                  }}
                >
                  確認
                </Button>
              </div>
            )}
            {type === "addNewForm" && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex" }}>
                  <CardMedia
                    onClick={() => {
                      setBrand("PM");
                    }}
                    component="img"
                    image={_pm}
                    alt=""
                    className={classes.media}
                    style={{
                      border: brand === "PM" ? "5px solid #454C96" : "",
                      borderRadius: "2.8rem",
                    }}
                  />
                  <CardMedia
                    onClick={() => {
                      setBrand("GOKU");
                    }}
                    component="img"
                    image={_goku}
                    alt=""
                    className={classes.media}
                    style={{
                      border: brand === "GOKU" ? "5px solid #454C96" : "",
                      borderRadius: "2.5rem",
                    }}
                  />
                </div>
                <Select
                  variant="outlined"
                  onChange={(event: any) => {
                    setArea(event.target.value);
                  }}
                  value={area}
                  className={classes.searchSelect}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    所屬地區
                  </MenuItem>
                  {areaLists?.map((v, i) => {
                    return (
                      <MenuItem key={i} value={v?.controlValue}>
                        {RenderArea(v?.controlValue)}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Button
                  variant="contained"
                  color={"primary"}
                  className={classes.button}
                  style={{ marginTop: "2rem", alignSelf: "center" }}
                  disabled={area === "" || brand === ""}
                  onClick={() => {
                    setGlobalData({
                      ...globalData,
                      targetInfo: staffInfo,
                      isHaveData: staffInfo?.length > 0 ? true : false,
                      brand,
                      area,
                      needPW: true,
                    });
                    navigate("/p/StaffInfoFormUnsigned", {
                      state: {
                        targetInfo: staffInfo,
                        isHaveData: staffInfo?.length > 0 ? true : false,
                        brand,
                        area,
                        needPW: true,
                      },
                    });
                    
                  }}
                >
                  確認
                </Button>
              </div>
            )}
            {type === "isNeedPW" && renderPwFields}
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

export default ModalContainer;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
  },
  title: { color: "#454C96", fontWeight: 500 },
  container: {
    // display: "flex",
    height: "100vh",
    borderRadius: "10rem",
  },

  input: {
    margin: theme.spacing(1, 0),
  },
  button: {
    margin: theme.spacing(1, 0),
    width: "9rem",
  },
  media: {
    width: "13rem",
    height: "13rem",
    margin: "1.5rem 0.5rem",

    cursor: "pointer",
  },
  resetImage: {
    width: "40px",
    height: "40px",
    margin: "5px",
    objectFit: "contain",
    cursor: "pointer",
    position: "absolute",
    bottom: 0,
    right: 0,
  },
  modal: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    width: "38rem",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    overflow: "hidden",
    outline: "none",
  },
  modalHeader: {
    backgroundColor: theme.palette.primary.main,
    width: "100%",
  },
  imageClose: {
    position: "absolute",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    height: 60,
    width: 60,
    padding: "20px",
    color: "#ffffff",
  },
  modalNavTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 60,
    fontSize: 20,
    fontWeight: 500,
    color: "#ffffff",
  },
  modalBody: {
    overflowY: "scroll",
    padding: "1.5rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  noRecord: {
    padding: "3rem 0rem",
    fontSize: 20,
  },
  signature: {
    border: "1px solid black",
    borderRadius: "5px",
    width: 500,
    height: 200,
    backgroundColor: "#F4EFFF",
    position: "relative",
  },
  searchSelect: {
    margin: "1.5rem 0",
    alignSelf: "center",
    backgroundColor: "#FFF",
    width: "22rem",
    height: "3rem",
    padding: "16px 0px",
    borderRadius: 4,
  },
  customOutlinedInput: {
    padding: "12px 14px",
  },
}));
